// src/components/SetupWizard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../utils/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import logo from '../assets/images/logo2.PNG';
import FacilitySetupWizard from './FacilitySetupWizard';
import TeamsWizard from './TeamsWizard';

// Simplified 3-step wizard for best-in-class onboarding
const WIZARD_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Pitchallo',
    component: 'WelcomeStep'
  },
  {
    id: 'facility',
    title: 'Set Up Your Facility',
    component: 'FacilityStep'
  },
  {
    id: 'teams',
    title: 'Add Your Teams',
    component: 'TeamsStep'
  },
  {
    id: 'complete',
    title: 'You\'re All Set!',
    component: 'CompleteStep'
  }
];

function SetupWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [user, setUser] = useState(null);
  const [clubName, setClubName] = useState('');
  const [clubId, setClubId] = useState('');
  const [loading, setLoading] = useState(true);

  // Load user data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      setUser(currentUser);
      
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();

          if (userData.clubId) {
            setClubId(userData.clubId);
            const clubDoc = await getDoc(doc(db, 'clubs', userData.clubId));
            if (clubDoc.exists()) {
              const clubData = clubDoc.data();
              setClubName(clubData.name || clubData.Name || clubData.clubName || '');
            }
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Complete the entire setup
  const completeSetup = async () => {
    if (!user) {
      navigate('/allocator/1');
      return;
    }

    try {
      await setDoc(doc(db, 'users', user.uid), {
        hasCompletedSetup: true,
        setupCompletedAt: new Date().toISOString()
      }, { merge: true });
      
      await auth.currentUser.reload();
      await new Promise(resolve => setTimeout(resolve, 500));
      
      navigate('/allocator/1', { replace: true });
    } catch (error) {
      console.error('Error completing setup:', error);
      navigate('/allocator/1', { replace: true });
    }
  };

  const nextStep = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleStepComplete = () => {
    nextStep();
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ 
          color: '#6b7280', 
          fontSize: '16px',
          textAlign: 'center'
        }}>
          <div style={{ 
            fontSize: '40px', 
            marginBottom: '16px' 
          }}>⚽</div>
          Loading...
        </div>
      </div>
    );
  }

  const progressPercentage = ((currentStep + 1) / WIZARD_STEPS.length) * 100;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Minimal Header */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '20px 32px'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <img 
              src={logo}
              alt="Pitchallo"
              style={{
                height: '36px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            {clubName && (
              <div style={{
                fontSize: '16px',
                color: '#6b7280',
                fontWeight: '500'
              }}>
                {clubName}
              </div>
            )}
          </div>
          
          {/* Progress indicator */}
          <div style={{
            fontSize: '14px',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            Step {currentStep + 1} of {WIZARD_STEPS.length}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{
        height: '4px',
        backgroundColor: '#e5e7eb',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          backgroundColor: '#3b82f6',
          width: `${progressPercentage}%`,
          transition: 'width 0.5s ease',
          position: 'absolute',
          left: 0,
          top: 0
        }} />
      </div>

      {/* Main Content Area */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '48px 32px'
      }}>
        {/* Step Content */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '48px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          minHeight: '600px'
        }}>
          {currentStep === 0 && (
            <WelcomeStep 
              clubName={clubName}
              onNext={nextStep}
            />
          )}
          
          {currentStep === 1 && (
            <FacilityStep 
              onComplete={handleStepComplete}
              clubId={clubId}
            />
          )}
          
          {currentStep === 2 && (
            <TeamsStep 
              onComplete={handleStepComplete}
              clubId={clubId}
            />
          )}
          
          {currentStep === 3 && (
            <CompleteStep 
              clubName={clubName}
              onComplete={completeSetup}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ===== STEP COMPONENTS =====

// Step 1: Welcome
function WelcomeStep({ clubName, onNext }) {
  return (
    <div style={{ 
      textAlign: 'center', 
      maxWidth: '600px', 
      margin: '0 auto',
      animation: 'fadeIn 0.5s ease'
    }}>
      <div style={{
        fontSize: '72px',
        marginBottom: '32px'
      }}>
        ⚽
      </div>
      
      <h1 style={{
        fontSize: '32px',
        fontWeight: '700',
        color: '#1f2937',
        marginBottom: '16px',
        letterSpacing: '-0.5px'
      }}>
        Welcome to Pitchallo
      </h1>
      
      <p style={{
        fontSize: '18px',
        color: '#6b7280',
        lineHeight: 1.7,
        marginBottom: '48px'
      }}>
        Let's get {clubName || 'your club'} set up with our set up wizard. The set up process should take you approximately 10 minutes. The step by step guide will help you 
        configure a map of your facility and add your teams. This will allow you to have a visual representation of your club grounds and you'll be able to share this map view with Club staff, parents & opponent clubs.
      </p>
      
      <div style={{
        backgroundColor: '#f0f9ff',
        border: '1px solid #bfdbfe',
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '48px',
        textAlign: 'left'
      }}>
        <h3 style={{
          margin: '0 0 20px 0',
          color: '#1e40af',
          fontSize: '18px',
          fontWeight: '600'
        }}>
          What's next?
        </h3>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
            <div style={{
              fontSize: '24px',
              minWidth: '32px'
            }}>
              📍
            </div>
            <div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#1e40af', 
                marginBottom: '4px' 
              }}>
                Your Facility
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                First you'll be asked to capture an arial image of your club & simply draw 4 dots in each corner for each of your pitch boundaries.
                This allows your users to get a visual of exactly where their sessions are taking place.
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
            <div style={{
              fontSize: '24px',
              minWidth: '32px'
            }}>
              👥
            </div>
            <div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#1e40af', 
                marginBottom: '4px' 
              }}>
                Your Teams
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Then you'll be asked to add your teams with names and colors for easy identification. Then you'll be able to start making pitch session allocations.
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <button
        onClick={onNext}
        style={{
          padding: '16px 48px',
          backgroundColor: '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          cursor: 'pointer',
          fontSize: '18px',
          fontWeight: '600',
          transition: 'all 0.2s ease',
          boxShadow: '0 4px 6px rgba(59, 130, 246, 0.3)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#2563eb';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 12px rgba(59, 130, 246, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#3b82f6';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 6px rgba(59, 130, 246, 0.3)';
        }}
      >
        Let's Get Started
      </button>
      
      <p style={{
        fontSize: '14px',
        color: '#9ca3af',
        marginTop: '24px'
      }}>
        Takes about 5-10 minutes
      </p>
    </div>
  );
}

// Step 2: Facility Setup
function FacilityStep({ onComplete, clubId }) {
  return (
    <div style={{ animation: 'fadeIn 0.5s ease' }}>
      <FacilitySetupWizard 
        onComplete={onComplete}
      />
    </div>
  );
}

// Step 3: Teams Setup
function TeamsStep({ onComplete, clubId }) {
  return (
    <div style={{ animation: 'fadeIn 0.5s ease' }}>
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: '8px'
        }}>
          Add Your Teams
        </h2>
        <p style={{
          fontSize: '16px',
          color: '#6b7280'
        }}>
          Create teams so you can start scheduling training and matches. 
        </p>
      </div>
      
      <TeamsWizard 
        clubId={clubId}
        onComplete={onComplete}
      />
    </div>
  );
}

// Step 4: Complete
function CompleteStep({ clubName, onComplete }) {
  return (
    <div style={{ 
      textAlign: 'center', 
      maxWidth: '600px', 
      margin: '0 auto',
      animation: 'fadeIn 0.5s ease'
    }}>
      <div style={{
        fontSize: '80px',
        marginBottom: '32px'
      }}>
        🎉
      </div>
      
      <h1 style={{
        fontSize: '32px',
        fontWeight: '700',
        color: '#1f2937',
        marginBottom: '16px'
      }}>
        You're All Set!
      </h1>
      
      <p style={{
        fontSize: '18px',
        color: '#6b7280',
        lineHeight: 1.7,
        marginBottom: '48px'
      }}>
        {clubName || 'Your club'} is ready to use Pitchallo. 
        Start creating allocations, managing schedules, and organizing your teams.
      </p>
      
      <div style={{
        backgroundColor: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '48px',
        textAlign: 'left'
      }}>
        <h3 style={{
          margin: '0 0 24px 0',
          color: '#15803d',
          fontSize: '20px',
          fontWeight: '600'
        }}>
          What's Next?
        </h3>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
            <div style={{ fontSize: '28px', minWidth: '36px' }}>📅</div>
            <div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#15803d', 
                marginBottom: '4px' 
              }}>
                Create Your First Allocation
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Add training sessions or matches to your pitches
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
            <div style={{ fontSize: '28px', minWidth: '36px' }}>🔗</div>
            <div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#15803d', 
                marginBottom: '4px' 
              }}>
                Share with Your Club
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Generate shareable links so everyone can see the schedule
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
            <div style={{ fontSize: '28px', minWidth: '36px' }}>👥</div>
            <div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#15803d', 
                marginBottom: '4px' 
              }}>
                Invite Team Members
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Add other club members to help manage allocations
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <button
        onClick={onComplete}
        style={{
          padding: '18px 56px',
          backgroundColor: '#10b981',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          cursor: 'pointer',
          fontSize: '20px',
          fontWeight: '700',
          transition: 'all 0.2s ease',
          boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)',
          letterSpacing: '0.5px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#059669';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 12px rgba(16, 185, 129, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#10b981';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.3)';
        }}
      >
        Go to Dashboard
      </button>
    </div>
  );
}

// Add fade-in animation
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
document.head.appendChild(style);

export default SetupWizard;
